import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Badge } from "@/components/ui/badge";
import { useLocation } from "@/components/location-provider";
import { PageHeader } from "@/components/page-header";
import { Waves, Gauge, Wind, Thermometer, RefreshCw } from "lucide-react";

interface MarineData {
  available: boolean;
  current: {
    waveHeightM: number | null;
    waveDirectionDeg: number | null;
    wavePeriodS: number | null;
    windWaveHeightM: number | null;
    swellWaveHeightM: number | null;
    seaSurfaceTempC: number | null;
  } | null;
  hourly: { time: string; waveHeightM: number | null; wavePeriodS: number | null; seaSurfaceTempC: number | null }[];
}

function compass(deg: number | null): string {
  if (deg == null) return "—";
  const dirs = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  return dirs[Math.round(deg / 22.5) % 16];
}

function seaState(heightM: number | null): string {
  if (heightM == null) return "—";
  if (heightM < 0.5) return "Calm";
  if (heightM < 1.25) return "Smooth";
  if (heightM < 2.5) return "Moderate";
  if (heightM < 4) return "Rough";
  if (heightM < 6) return "Very rough";
  return "High";
}

export default function CoastalPage() {
  const [data, setData] = useState<MarineData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const { current: location } = useLocation();

  useEffect(() => {
    setLoading(true);
    fetch(`/api/marine?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [location.lat, location.lon, refresh]);

  const chartData = (data?.hourly || []).map((h) => ({
    time: new Date(h.time).toLocaleTimeString([], { hour: "numeric", weekday: "short" }),
    wave: h.waveHeightM ?? null,
    temp: h.seaSurfaceTempC ?? null,
  }));

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader
        eyebrow="Coastal"
        title="Marine Conditions"
        subtitle={`${location.name}, ${location.country}`}
        action={
          <GlassPanel intensity="low" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs !rounded-full">
            <Waves className="h-3 w-3 text-primary" />
            <span className="font-semibold">Open-Meteo Marine</span>
          </GlassPanel>
        }
      />

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-56 rounded-2xl" />
          <Skeleton className="h-72 rounded-2xl" />
        </div>
      ) : !data || !data.available || !data.current ? (
        <GlassPanel intensity="low" className="p-16 text-center">
          <Waves className="h-10 w-10 text-muted-foreground/40 mx-auto mb-4" />
          <h2 className="text-xl font-bold">No marine data</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
            Wave and sea-surface data is not available for this location. Try a coastal location.
          </p>
        </GlassPanel>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Metric icon={Waves} label="Wave Height" value={`${data.current.waveHeightM?.toFixed(2) ?? "—"} m`} sub={seaState(data.current.waveHeightM)} />
            <Metric icon={Gauge} label="Wave Period" value={`${data.current.wavePeriodS?.toFixed(1) ?? "—"} s`} sub="Peak" />
            <Metric icon={Gauge} label="Direction" value={`${data.current.waveDirectionDeg ?? "—"}°`} sub={compass(data.current.waveDirectionDeg)} />
            <Metric icon={Waves} label="Swell" value={`${data.current.swellWaveHeightM?.toFixed(2) ?? "—"} m`} sub="Open-ocean" />
            <Metric icon={Wind} label="Wind Waves" value={`${data.current.windWaveHeightM?.toFixed(2) ?? "—"} m`} sub="Local" />
            <Metric icon={Thermometer} label="Sea Temp" value={`${data.current.seaSurfaceTempC != null ? Math.round(data.current.seaSurfaceTempC) : "—"}°`} sub="Surface" />
          </div>

          <GlassPanel intensity="low" className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Wave Height · 48h</h3>
              <Badge variant="outline" size="sm">m</Badge>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="waveGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="oklch(0.7 0.19 200)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="oklch(0.7 0.19 200)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval={5} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} unit="m" />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, fontSize: 12, border: "1px solid var(--border)", background: "var(--popover)", color: "var(--popover-foreground)" }}
                    labelStyle={{ color: "var(--foreground)", fontWeight: 600 }}
                    cursor={{ stroke: "var(--border)", strokeWidth: 1 }}
                  />
                  <Area type="monotone" dataKey="wave" stroke="oklch(0.7 0.19 200)" fill="url(#waveGrad)" strokeWidth={2} name="Wave height" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </GlassPanel>

          <div className="text-center">
            <button
              onClick={() => setRefresh((n) => n + 1)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary/10 px-4 py-2 text-xs font-semibold text-primary hover:bg-primary/15 transition-colors cursor-pointer"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Waves;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <GlassPanel intensity="low" hover className="p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <p className="text-xl font-bold font-mono tracking-tight">{value}</p>
      <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>
    </GlassPanel>
  );
}
